import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@cloudscape-design/components/header';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Container from '@cloudscape-design/components/container';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Grid from '@cloudscape-design/components/grid';
import Button from '@cloudscape-design/components/button';
import Icon from '@cloudscape-design/components/icon';
import { useAuth } from '../context/AuthContext';
import { getSessionHistory } from '../services/sessions';
import type { Session } from '../types';

/**
 * Home Page - Main landing page after login.
 *
 * Provides a welcoming entry point that helps users:
 * - Quickly jump into a training program
 * - See their recent activity at a glance
 * - Understand what programs are available
 */
export function HomePage(): React.ReactElement {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recentSessions, setRecentSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchRecent() {
      try {
        const data = await getSessionHistory();
        setRecentSessions(data.slice(0, 3));
      } catch {
        // Non-critical - just won't show recent activity
      } finally {
        setLoading(false);
      }
    }
    fetchRecent();
  }, []);

  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <ContentLayout
      data-testid="home-page"
      header={
        <Header
          variant="h1"
          description="Select a training program to get started, or pick up where you left off."
        >
          Welcome back, {firstName}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Training Programs Section */}
        <Header variant="h2">Training Programs</Header>
        <Grid
          gridDefinition={[
            { colspan: { default: 12, m: 6 } },
            { colspan: { default: 12, m: 6 } },
          ]}
        >
          {/* Business Acumen for SA */}
          <Container
            header={
              <Header
                variant="h3"
                description="A dynamic, immersive crash course to navigate complex business conversations"
              >
                SA Business Acumen Program
              </Header>
            }
            footer={
              <Button
                variant="primary"
                onClick={() => navigate('/programs/business-acumen')}
              >
                Open program
              </Button>
            }
          >
            <SpaceBetween size="s">
              <Box variant="p">
                Get hands-on experience navigating complex business conversations through
                engaging role-playing scenarios. Practice strategic decisions that align your
                technical expertise with broader business objectives — in a safe environment
                with immediate AI-powered feedback.
              </Box>
              <Box variant="small" color="text-status-info">
                <Icon name="status-info" /> Voice role play with real-time AI evaluation
              </Box>
            </SpaceBetween>
          </Container>

          {/* Placeholder for future programs */}
          <Container
            header={
              <Header
                variant="h3"
                description="More programs coming soon"
              >
                Additional Programs
              </Header>
            }
          >
            <SpaceBetween size="s">
              <Box variant="p" color="text-body-secondary">
                We're working on expanding the training catalog. Stay tuned for new programs
                covering technical skills, leadership development, and more.
              </Box>
              <Box variant="small" color="text-status-inactive">
                <Icon name="status-pending" /> Coming soon
              </Box>
            </SpaceBetween>
          </Container>
        </Grid>

        {/* Quick Actions */}
        {recentSessions.length > 0 && !loading && (
          <Container
            header={
              <Header
                variant="h2"
                actions={
                  <Button
                    variant="link"
                    onClick={() => navigate('/my-progress')}
                  >
                    View all progress
                  </Button>
                }
              >
                Recent Activity
              </Header>
            }
          >
            <SpaceBetween size="s">
              {recentSessions.map((session) => (
                <Box key={session.id} padding={{ vertical: 'xs' }}>
                  <SpaceBetween direction="horizontal" size="s">
                    <Box variant="strong">{session.scenarioName}</Box>
                    <Box variant="small" color="text-body-secondary">
                      {new Date(session.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </Box>
                    {session.score !== null && (
                      <Box variant="small" color="text-status-success">
                        Score: {session.score}
                      </Box>
                    )}
                  </SpaceBetween>
                </Box>
              ))}
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}

export default HomePage;
